import _ from 'lodash';
import {
  CHARACTERIZATION_GREEN,
  CHARACTERIZATION_RED,
  CHARACTERIZATION_YELLOW
} from '../../../constants/practice-characterization-evaluation';
import { addRange } from '../../../utils/arrayExtensions';
import { PRACTICE_CHARACTERIZATION_EVALUATION_VALUES } from '../constants/practice-characterization-evaluation-values';

const characterizationEvaluationToCellValueMap = new Map([
  [CHARACTERIZATION_GREEN, PRACTICE_CHARACTERIZATION_EVALUATION_VALUES.SATISFIED],
  [CHARACTERIZATION_YELLOW, PRACTICE_CHARACTERIZATION_EVALUATION_VALUES.PARTIAL],
  [CHARACTERIZATION_RED, PRACTICE_CHARACTERIZATION_EVALUATION_VALUES.DEFICIENT]
]);

export const AppraisalReportsService = {
  getAppraisalOrgUnitDetailedReportData: (
    appraisalOrgUnit,
    maxLevelsWithinGroups,
    isPercentageVisible,
    existingPracticeAreas
  ) => {
    const reportData = appraisalOrgUnit.AppraisalOrgUnitTargetPracticeAreas.map(
      appraisalPracticeArea => {
        const practices = getPractices(
          maxLevelsWithinGroups,
          appraisalPracticeArea.PracticeArea.Id,
          existingPracticeAreas,
          appraisalOrgUnit.AppraisalOrgUnitTargetPractices
        );

        const aggregatePercentage = isPercentageVisible
          ? {
              appraisalOrgUnitAggregatePercentage: appraisalOrgUnit.AggregatePercentage,
              practiceAreaPercentage: appraisalPracticeArea.AggregatePercentage
            }
          : null;

        const practiceAreaRow = {
          practiceArea: {
            id: appraisalPracticeArea.PracticeArea.Id,
            displayName: `${appraisalPracticeArea.PracticeArea.Name} (${appraisalPracticeArea.PracticeArea.Acronym})`,
            acronym: appraisalPracticeArea.PracticeArea.Acronym
          },
          practices,
          aggregatePercentage
        };

        return practiceAreaRow;
      }
    );

    return _.orderBy(reportData, rd => rd.practiceArea.displayName);
  },

  getMaxLevelWithinGroups: (appraisalOrgUnits, includeHigherLevels, practiceAreas) => {
    const groupLevels = getPracticeGroupLevels(
      appraisalOrgUnits.flatMap(aou => aou.AppraisalOrgUnitTargetPracticeAreas),
      practiceAreas,
      includeHigherLevels
    );

    const maxGroupLevel = _.max(groupLevels);

    const result = _.range(1, maxGroupLevel + 1).map(groupLevel => ({
      groupLevel: groupLevel,
      maxNumberWithinLevel: getMaximumNumberWithinPractices(
        groupLevel,
        includeHigherLevels,
        appraisalOrgUnits,
        practiceAreas
      )
    }));

    return result;
  }
};

const getPracticeGroupLevels = (appraisalPracticeAreas, practiceAreas, includeHigherLevels) => {
  if (includeHigherLevels) {
    return appraisalPracticeAreas
      .map(aoupa => practiceAreas.find(pa => pa.Id === aoupa.PracticeArea.Id))
      .flatMap(pa => pa.PracticeGroups)
      .map(pg => pg.Level);
  }

  return appraisalPracticeAreas.map(aoupa => aoupa.PracticeGroup.Level);
};

const getMaximumNumberWithinPractices = (
  level,
  includeHigherLevels,
  appraisalOrgUnits,
  practiceAreas
) => {
  const practices = includeHigherLevels
    ? appraisalOrgUnits
        .flatMap(aou => aou.AppraisalOrgUnitTargetPracticeAreas)
        .flatMap(aoupa => practiceAreas.find(pa => pa.Id === aoupa.PracticeArea.Id).PracticeGroups)
        .filter(pg => pg.Level === level)
        .flatMap(pg => pg.Practices)
    : appraisalOrgUnits
        .flatMap(aou => aou.AppraisalOrgUnitTargetPractices)
        .filter(p => p.PracticeGroup.Level === level)
        .map(aoup => aoup.Practice);

  if (!practices.length) {
    return 0;
  }

  return _.maxBy(practices, p => p.NumberWithinLevel).NumberWithinLevel;
};

const getPractices = (
  maxLevelsWithinGroup,
  appraisalPracticeAreaId,
  practiceAreas,
  appraisalOrgUnitPractices
) => {
  const practices = [];

  for (const group of maxLevelsWithinGroup) {
    const groupPracticeLevels = _.range(1, group.maxNumberWithinLevel + 1);
    const groupPractices = groupPracticeLevels.map(practiceLevel =>
      getPractice(
        group.groupLevel,
        practiceLevel,
        appraisalPracticeAreaId,
        practiceAreas,
        appraisalOrgUnitPractices
      )
    );

    addRange(practices, groupPractices);
  }

  return practices;
};

const getPractice = (
  practiceGroupLevel,
  numberWithinLevel,
  appraisalPracticeAreaId,
  practiceAreas,
  appraisalOrgUnitPractices
) => {
  const appraisalOrgUnitPractice = appraisalOrgUnitPractices.find(
    p =>
      p.PracticeGroup.Level === practiceGroupLevel &&
      p.PracticeGroup.PracticeArea.Id === appraisalPracticeAreaId &&
      p.Practice.NumberWithinLevel === numberWithinLevel
  );

  const characterizationEvaluation = getCharacterizationEvaluationCellValue(
    appraisalOrgUnitPractice,
    practiceGroupLevel,
    numberWithinLevel,
    appraisalPracticeAreaId,
    practiceAreas
  );

  return {
    id: appraisalOrgUnitPractice ? appraisalOrgUnitPractice.Id : null,
    name: appraisalOrgUnitPractice ? appraisalOrgUnitPractice.Practice.Name : null,
    groupLevel: practiceGroupLevel,
    numberWithinLevel: numberWithinLevel,
    characterizationEvaluation
  };
};

const getCharacterizationEvaluationCellValue = (
  practice,
  practiceGroupLevel,
  numberWithinLevel,
  appraisalPracticeAreaId,
  practiceAreas
) => {
  if (practice) {
    return characterizationEvaluationToCellValueMap.get(practice.CharacterizationsEvaluation);
  }

  const practiceGroup = practiceAreas
    .find(pa => pa.Id === appraisalPracticeAreaId)
    .PracticeGroups.find(pg => pg.Level === practiceGroupLevel);

  if (
    practiceGroup &&
    practiceGroup.Practices.find(p => p.NumberWithinLevel === numberWithinLevel)
  ) {
    return PRACTICE_CHARACTERIZATION_EVALUATION_VALUES.UNAVAILABLE;
  }

  return PRACTICE_CHARACTERIZATION_EVALUATION_VALUES.NOT_SET;
};
